import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Phone, MessageCircle, Globe, Mail, HeartHandshake } from 'lucide-react';
import { Button } from '@/components/ui/button';

const supportData = {
  header: {
    title: "You're Not Alone — Support Is Available",
    body: `If you're feeling unsure or unsettled, you don't have to figure it out alone. It's okay not to have clarity yet — abuse is often confusing, especially when it isn't physical.

If you feel unsafe, controlled, afraid to speak, or constantly blamed, that matters. You deserve care, understanding, and support.

The resources below are here to help you explore options and connect with support, at your own pace.`
  },
  sections: [
    {
      title: "Emergency & Crisis Support (24/7)",
      items: [
        {
          name: "National Domestic Violence Hotline",
          details: [
            "Call: 1-800-799-SAFE (7233)",
            "Text: START to 88788",
            "Chat: thehotline.org",
            "Confidential support, safety planning, and local resources."
          ]
        },
        {
          name: "National Sexual Assault Hotline (RAINN)",
          details: [
            "Call: 1-800-656-HOPE (4673)",
            "Chat: rainn.org",
            "Confidential support for survivors of sexual violence."
          ]
        },
        {
          name: "VictimConnect Resource Center",
          details: [
            "Call or Text: 1-855-4-VICTIM (1-855-484-2846)",
            "Website: victimconnect.org",
            "Support navigating options and connecting with local resources. Available in multiple languages."
          ]
        }
      ]
    },
    {
      title: "Youth, Teens & Family Support",
      items: [
        {
          name: "Covenant House / National Runaway Safeline",
          details: [
            "Call: 800-999-9999 or 1-800-RUNAWAY",
            "Website: nineline.org"
          ]
        },
        {
          name: "Children of the Night",
          details: [
            "24-Hour Hotline: (800) 551-1300",
            "Website: childrenofthenight.org"
          ]
        },
        {
          name: "KidsHealth",
          details: [
            "Website: kidshealth.org/en/teens/abuse.html"
          ]
        }
      ]
    },
    {
      title: "Mental Health & Emotional Support",
      items: [
        {
          name: "National Alliance on Mental Illness (NAMI)",
          details: [
            "Helpline: 800-950-NAMI (6264)",
            "Website: nami.org"
          ]
        },
        {
          name: "National Maternal Mental Health Hotline",
          details: [
            "Call or Text: 833-TLC-MAMA (833-852-6262)",
            "24/7 confidential support"
          ]
        },
        {
          name: "Start Your Recovery",
          details: [
            "Website: startyourrecovery.org"
          ]
        }
      ]
    },
    {
      title: "Legal Support & Advocacy",
      items: [
        {
          name: "WomensLaw.org",
          details: [
            "Legal information on protection orders, custody, and survivor rights."
          ]
        },
        {
          name: "DV LEAP (Domestic Violence Legal Empowerment & Appeals Project)",
          details: [
            "Phone: (202) 742-1727 (option 2)",
            "Website: volare-empowers.org/appeals"
          ]
        },
        {
          name: "Legal Momentum",
          details: [
            "Website: legalmomentum.org"
          ]
        },
        {
          name: "National Crime Victim Bar Association",
          details: [
            "Lawyer Referral Line: 800-FYI-CALL",
            "Website: victimsofcrime.org"
          ]
        },
        {
          name: "National Crime Victim Law Institute (NCVLI)",
          details: [
            "Website: ncvli.org"
          ]
        },
        {
          name: "National Defense Center for Criminalized Survivors",
          details: [
            "Phone: 800-903-0111 ext. 3",
            "Website: bwjp.org"
          ]
        }
      ]
    },
    {
      title: "Additional Support & Advocacy",
      items: [
        {
          name: "National Center on Domestic Violence, Trauma & Mental Health",
          details: [
            "Website: ncdvtmh.org"
          ]
        },
        {
          name: "National Resource Center on Domestic Violence",
          details: [
            "Website: nrcdv.org"
          ]
        },
        {
          name: "National Network to End Domestic Violence (NNEDV)",
          details: [
            "Website: nnedv.org"
          ]
        }
      ]
    },
    {
      title: "Immigration & International Support",
      items: [
        {
          name: "Immigration Equality",
          details: [
            "Phone: (212) 714-2904",
            "Website: immigrationequality.org"
          ]
        },
        {
          name: "The Narkis Golan International Child Abduction Initiative",
          details: [
            "Website: sanctuaryforfamilies.org/ngi",
            "Email: GolanInitiative@sffny.org"
          ]
        }
      ]
    }
  ],
  footer: "This resource list is provided for general informational purposes only. Not every organization listed has been individually vetted, and availability may vary by location. This content is not a substitute for emergency services, medical care, or legal advice. If you are in immediate danger, please contact local emergency services right away."
};

// Helper to detect and render links in text
const renderWithLinks = (text) => {
  // Match URLs and common patterns
  const urlPattern = /(https?:\/\/[^\s]+|[a-zA-Z0-9-]+\.(org|com|gov|net|edu)(\/[^\s]*)?)/gi;
  const phonePattern = /(\d{3}[-.\s]?\d{3}[-.\s]?\d{4}|\d{1,3}[-.\s]?\d{3}[-.\s]?\d{3}[-.\s]?\d{4}|1-800-[A-Z0-9-]+|800-[A-Z0-9-]+)/gi;
  
  const parts = text.split(/(\s+)/);
  
  return parts.map((part, idx) => {
    // Check if it's a URL
    if (urlPattern.test(part)) {
      const url = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={idx}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};

// Get icon for detail type
const getDetailIcon = (detail) => {
  if (detail.toLowerCase().includes('call') || detail.toLowerCase().includes('phone') || detail.toLowerCase().includes('hotline')) {
    return <Phone className="w-4 h-4 flex-shrink-0" style={{ color: '#C98F78' }} />;
  }
  if (detail.toLowerCase().includes('text') || detail.toLowerCase().includes('chat')) {
    return <MessageCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#C98F78' }} />;
  }
  if (detail.toLowerCase().includes('website') || detail.toLowerCase().includes('.org') || detail.toLowerCase().includes('.com')) {
    return <Globe className="w-4 h-4 flex-shrink-0" style={{ color: '#C98F78' }} />;
  }
  if (detail.toLowerCase().includes('email')) {
    return <Mail className="w-4 h-4 flex-shrink-0" style={{ color: '#C98F78' }} />;
  }
  return null;
};

const AccordionSection = ({ title, items, isOpen, onToggle, sectionRef }) => {
  return (
    <div 
      ref={sectionRef}
      className="border rounded-lg overflow-hidden mb-3"
      style={{ 
        borderColor: 'var(--border-color)',
        background: 'var(--bg-card)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-4 py-4 flex items-center justify-between text-left transition-colors hover:bg-opacity-50"
        style={{ 
          background: isOpen ? 'rgba(216, 164, 143, 0.1)' : 'transparent'
        }}
        aria-expanded={isOpen}
      >
        <span 
          className="font-semibold text-base"
          style={{ color: 'var(--text-primary)' }}
        >
          {title}
        </span>
        <ChevronDown 
          className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: '#C98F78' }}
        />
      </button>
      
      {isOpen && (
        <div 
          className="px-4 pb-4 space-y-4"
          style={{ borderTop: '1px solid var(--border-color)' }}
        >
          {items.map((item, idx) => (
            <div key={idx} className="pt-4">
              <h4 
                className="font-medium mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                {item.name}
              </h4>
              <ul className="space-y-2">
                {item.details.map((detail, detailIdx) => (
                  <li 
                    key={detailIdx}
                    className="flex items-start gap-2 text-sm"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {getDetailIcon(detail)}
                    <span className="leading-relaxed">{renderWithLinks(detail)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SupportResourcesModal = ({ isOpen, onClose }) => {
  const [openSection, setOpenSection] = useState(null);
  const sectionRefs = useRef({});
  const modalContentRef = useRef(null);

  // Handle close and reset state
  const handleClose = () => {
    setOpenSection(null);
    onClose();
  };

  // Auto-scroll to active accordion section
  useEffect(() => {
    if (openSection !== null && sectionRefs.current[openSection]) {
      setTimeout(() => {
        sectionRefs.current[openSection]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [openSection]);

  const toggleSection = (index) => {
    // If clicking the same section, close it; otherwise open the new one (closing any other)
    setOpenSection(prev => prev === index ? null : index);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-modal-title"
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal Content */}
      <div 
        ref={modalContentRef}
        className="relative w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:mx-4 sm:rounded-2xl overflow-hidden flex flex-col"
        style={{ 
          background: 'var(--bg-primary)',
        }}
      >
        {/* Header */}
        <div 
          className="sticky top-0 z-10 px-4 sm:px-6 py-4 flex items-center justify-between"
          style={{ 
            background: 'linear-gradient(to right, #D8A48F, #C98F78)',
            borderBottom: '1px solid rgba(255,255,255,0.2)'
          }}
        >
          <div className="flex items-center gap-3">
            <HeartHandshake className="w-6 h-6 text-white" />
            <h2 
              id="support-modal-title"
              className="text-lg font-semibold text-white"
            >
              Support Resources
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-white hover:bg-white/20 rounded-full"
            aria-label="Close support resources"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
          {/* Intro Section */}
          <div className="mb-6">
            <h3 
              className="text-xl font-semibold mb-4"
              style={{ color: 'var(--text-primary)' }}
            >
              {supportData.header.title}
            </h3>
            <p 
              className="text-sm leading-relaxed whitespace-pre-line"
              style={{ color: 'var(--text-secondary)' }}
            >
              {supportData.header.body}
            </p>
          </div>

          {/* Accordion Sections */}
          <div className="space-y-1">
            {supportData.sections.map((section, index) => (
              <AccordionSection
                key={index}
                title={section.title}
                items={section.items}
                isOpen={openSection === index}
                onToggle={() => toggleSection(index)}
                sectionRef={(el) => sectionRefs.current[index] = el}
              />
            ))}
          </div>

          {/* Footer Disclaimer */}
          <div 
            className="mt-6 p-4 rounded-lg text-xs leading-relaxed"
            style={{ 
              background: 'rgba(216, 164, 143, 0.1)',
              color: 'var(--text-secondary)'
            }}
          >
            {supportData.footer}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportResourcesModal;
